import React, { useState, useEffect, useMemo, useRef } from 'react';
import Modal from '../Modal';
import { colaboradoresService, type Colaborador } from '../../services/colaboradores.service';
import type { DesligamentoSemDemissionalPayloadContract } from '@sstexames/shared-types';
import { useToast, ToastContainer } from '../Toast';
import { useQueryClient } from '@tanstack/react-query';
import {
  UserMinusIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowsRightLeftIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { formatCPF } from '../../utils/formatters';

export interface ModalDesligamentoSemDemissionalProps {
  isOpen: boolean;
  onClose: () => void;
  empresaId: number;
  colaborador?: Colaborador | null;
  colaboradoresAtivos?: Colaborador[];
  onSuccess?: () => void;
}

const CHIPS_JUSTIFICATIVAS = [
  'Demissão em período de experiência (<90 dias)',
  'Colaborador já desligado antes da migração do sistema',
  'Exame periódico realizado recentemente conforme NR-07',
  'Não comparecimento / Abandono de emprego',
  'Acordo de rescisão sem exigência de novo exame',
];

const MOTIVOS_DESLIGAMENTO = [
  'Término de contrato de experiência (<90 dias)',
  'Demissão sem justa causa',
  'Pedido de demissão pelo colaborador',
  'Demissão com justa causa',
  'Desligamento anterior à migração do sistema',
  'Rescisão por acordo mútuo',
  'Outro motivo operacional',
];

export const ModalDesligamentoSemDemissional: React.FC<ModalDesligamentoSemDemissionalProps> = ({
  isOpen,
  onClose,
  empresaId,
  colaborador,
  colaboradoresAtivos = [],
  onSuccess,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const [submitting, setSubmitting] = useState(false);
  const [errorAlert, setErrorAlert] = useState<string | null>(null);
  const [selectedColab, setSelectedColab] = useState<Colaborador | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [internalColabs, setInternalColabs] = useState<Colaborador[]>([]);
  const [loadingColabs, setLoadingColabs] = useState(false);

  const [dataDemissao, setDataDemissao] = useState('');
  const [motivoDesligamento, setMotivoDesligamento] = useState(MOTIVOS_DESLIGAMENTO[0]);
  const [justificativa, setJustificativa] = useState('');
  const [declaracaoCiencia, setDeclaracaoCiencia] = useState(false);

  const getTodayLocal = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Inicializar estado ao abrir modal ou trocar colaborador pré-selecionado
  useEffect(() => {
    if (!isOpen) {
      setErrorAlert(null);
      return;
    }

    const today = getTodayLocal();
    setErrorAlert(null);
    setDataDemissao(today);
    setJustificativa('');
    setDeclaracaoCiencia(false);
    setSearchQuery('');
    setIsDropdownOpen(false);

    if (colaborador) {
      setSelectedColab(colaborador);
    } else {
      setSelectedColab(null);
    }

    // Se a lista de colaboradores ativos passada estiver vazia, buscar do backend
    if (colaboradoresAtivos.length === 0 && empresaId) {
      setLoadingColabs(true);
      colaboradoresService
        .findAll({ limit: 1000, enabled: true, empresa_id: empresaId })
        .then((res) => {
          setInternalColabs(res.data || []);
        })
        .catch((err) => {
          console.error('Erro ao carregar colaboradores ativos para busca:', err);
        })
        .finally(() => {
          setLoadingColabs(false);
        });
    }
  }, [colaborador, isOpen, empresaId, colaboradoresAtivos.length]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Lista consolidada de colaboradores ativos
  const activeColabsList = useMemo(() => {
    if (colaboradoresAtivos && colaboradoresAtivos.length > 0) {
      return colaboradoresAtivos;
    }
    return internalColabs;
  }, [colaboradoresAtivos, internalColabs]);

  // Filtragem dinâmica por nome ou CPF
  const filteredColabs = useMemo(() => {
    if (!searchQuery.trim()) {
      return activeColabsList.slice(0, 30);
    }
    const q = searchQuery.toLowerCase().trim();
    const qClean = q.replace(/\D/g, '');

    return activeColabsList.filter((c) => {
      const matchName = c.nome?.toLowerCase().includes(q) || (c.nome_social && c.nome_social.toLowerCase().includes(q));
      const matchCpf = c.cpf && (c.cpf.includes(q) || (qClean && c.cpf.replace(/\D/g, '').includes(qClean)));
      const cargoNome = c.cargo?.nome || (c as any).cargo_nome;
      const setorNome = c.setor?.nome || (c as any).setor_nome;
      const matchCargo = cargoNome && cargoNome.toLowerCase().includes(q);
      const matchSetor = setorNome && setorNome.toLowerCase().includes(q);
      return matchName || matchCpf || matchCargo || matchSetor;
    });
  }, [activeColabsList, searchQuery]);

  const handleApplyChip = (text: string) => {
    if (!justificativa.trim()) {
      setJustificativa(text);
    } else if (!justificativa.includes(text)) {
      setJustificativa(`${justificativa}. ${text}`);
    }
  };

  const handleSelectColaborador = (c: Colaborador) => {
    setSelectedColab(c);
    setIsDropdownOpen(false);
    setSearchQuery('');
  };

  const handleClearSelection = () => {
    setSelectedColab(null);
    setSearchQuery('');
    setIsDropdownOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorAlert(null);

    if (!selectedColab?.id) {
      const msg = 'Pesquise e selecione o colaborador a ser desligado';
      setErrorAlert(msg);
      toast.error(msg);
      return;
    }

    if (!dataDemissao) {
      const msg = 'Informe a data de demissão';
      setErrorAlert(msg);
      toast.error(msg);
      return;
    }

    const todayStr = getTodayLocal();
    if (dataDemissao > todayStr) {
      const msg = 'A data de desligamento deve ser retroativa (não pode ser superior à data de hoje)';
      setErrorAlert(msg);
      toast.error(msg);
      return;
    }

    if (!justificativa.trim() || justificativa.trim().length < 10) {
      const msg = 'Informe uma justificativa válida com no mínimo 10 caracteres';
      setErrorAlert(msg);
      toast.error(msg);
      return;
    }

    if (!declaracaoCiencia) {
      const msg = 'É obrigatório aceitar o termo de ciência e responsabilidade';
      setErrorAlert(msg);
      toast.error(msg);
      return;
    }

    try {
      setSubmitting(true);
      const payload: DesligamentoSemDemissionalPayloadContract = {
        empresa_id: Number(selectedColab?.empresa_id || empresaId),
        colaborador_id: Number(selectedColab.id),
        data_demissao: dataDemissao,
        motivo_desligamento: motivoDesligamento,
        justificativa: justificativa.trim(),
        declaracao_ciencia: true,
      };

      const res = await colaboradoresService.desligamentoSemDemissional(payload);
      toast.success(res.message || 'Desligamento registrado com sucesso! Redirecionando para Movimentações...');

      queryClient.invalidateQueries({ queryKey: ['colaboradores'] });
      queryClient.invalidateQueries({ queryKey: ['organizacao'] });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      const rawMsg = err?.response?.data?.message || err?.message || 'Erro ao registrar desligamento';
      const msg = Array.isArray(rawMsg) ? rawMsg.join('. ') : rawMsg;
      setErrorAlert(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Registrar Desligamento (Alguém não deveria estar aqui?)"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4 text-sm text-gray-700">
        {/* Aviso de Alerta e Regulatória (Cores claras e limpas) */}
        <div className="bg-rose-50/70 border border-rose-200 p-3 rounded-lg flex items-start gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-rose-900 leading-relaxed">
            <span className="font-semibold">Ajuste de Quadro sem Exame Demissional:</span> Esta operação registrará
            a saída retroativa do colaborador, desabilitando-o do quadro ativo, bloqueando novos exames e cancelando
            agendamentos pendentes futuros.
          </div>
        </div>

        {/* Banner de Erro em Destaque */}
        {errorAlert && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-xs p-3.5 rounded-lg flex items-start gap-3 animate-fadeIn">
            <ExclamationCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 font-medium leading-relaxed">{errorAlert}</div>
            <button
              type="button"
              onClick={() => setErrorAlert(null)}
              className="text-red-400 hover:text-red-600 transition-colors p-0.5 cursor-pointer"
              title="Fechar alerta"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Seleção do Colaborador via Input Search */}
        {selectedColab ? (
          <div className="bg-white border border-gray-200 rounded-lg p-3.5 shadow-xs flex items-center justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                  Colaborador Selecionado
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  Ativo no quadro
                </span>
              </div>
              <div className="text-sm font-bold text-gray-900">{selectedColab.nome}</div>
              <div className="text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1 pt-0.5">
                <span>
                  CPF: <strong className="text-gray-800 font-mono">{formatCPF(selectedColab.cpf)}</strong>
                </span>
                <span>
                  Cargo: <strong className="text-gray-800">{selectedColab.cargo?.nome || (selectedColab as any).cargo_nome || 'Sem cargo'}</strong>
                </span>
                <span>
                  Setor: <strong className="text-gray-800">{selectedColab.setor?.nome || (selectedColab as any).setor_nome || 'Sem setor'}</strong>
                </span>
                {selectedColab.data_admissao && (
                  <span>
                    Admissão:{' '}
                    <strong className="text-gray-800">
                      {new Date(selectedColab.data_admissao).toLocaleDateString('pt-BR')}
                    </strong>
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleClearSelection}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-colors cursor-pointer flex-shrink-0"
              title="Trocar colaborador selecionado"
            >
              <ArrowsRightLeftIcon className="w-3.5 h-3.5 text-gray-500" />
              <span>Trocar</span>
            </button>
          </div>
        ) : (
          <div className="relative" ref={searchContainerRef}>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Buscar Colaborador <span className="text-red-500 font-bold">*</span>
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setIsDropdownOpen(true);
                }}
                onFocus={() => setIsDropdownOpen(true)}
                placeholder="Digite o nome ou CPF para pesquisar..."
                className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-colors"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Dropdown com Resultados da Busca */}
            {isDropdownOpen && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto divide-y divide-gray-100">
                {loadingColabs ? (
                  <div className="p-3 text-xs text-gray-500 text-center">
                    Carregando colaboradores ativos...
                  </div>
                ) : filteredColabs.length === 0 ? (
                  <div className="p-3 text-xs text-gray-500 text-center">
                    {searchQuery
                      ? 'Nenhum colaborador ativo encontrado com esse termo.'
                      : 'Nenhum colaborador ativo cadastrado.'}
                  </div>
                ) : (
                  filteredColabs.map((c) => {
                    const cCargo = c.cargo?.nome || (c as any).cargo_nome;
                    const cSetor = c.setor?.nome || (c as any).setor_nome;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => handleSelectColaborador(c)}
                        className="w-full text-left p-2.5 hover:bg-rose-50/70 transition-colors flex items-center justify-between cursor-pointer group"
                      >
                        <div>
                          <div className="text-xs font-bold text-gray-900 group-hover:text-rose-700">
                            {c.nome}
                          </div>
                          <div className="text-[11px] text-gray-500 flex items-center gap-2 mt-0.5">
                            <span className="font-mono">CPF: {formatCPF(c.cpf)}</span>
                            <span>•</span>
                            <span>{cCargo || 'Sem cargo'}</span>
                            {cSetor && (
                              <>
                                <span>•</span>
                                <span>{cSetor}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <span className="text-[11px] text-rose-600 font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                          Selecionar →
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
            <span className="text-[11px] text-gray-500 mt-1 block">
              Pesquise pelo nome completo, nome social, CPF ou cargo.
            </span>
          </div>
        )}

        {/* Data e Motivo */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Data de Desligamento / Demissão <span className="text-red-500 font-bold">*</span>
            </label>
            <input
              type="date"
              required
              max={new Date().toISOString().split('T')[0]}
              value={dataDemissao}
              onChange={(e) => setDataDemissao(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
            />
            <span className="text-[11px] text-gray-500 mt-1 block font-medium">Obrigatório data retroativa (até hoje)</span>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Motivo do Desligamento <span className="text-red-500 font-bold">*</span>
            </label>
            <select
              required
              value={motivoDesligamento}
              onChange={(e) => setMotivoDesligamento(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
            >
              {MOTIVOS_DESLIGAMENTO.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Chips de Preenchimento Rápido */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
            Opções Rápidas de Justificativa (clique para preencher):
          </label>
          <div className="flex flex-wrap gap-1.5">
            {CHIPS_JUSTIFICATIVAS.map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => handleApplyChip(chip)}
                className="text-[11px] bg-gray-50 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300 text-gray-700 border border-gray-200 px-2.5 py-1 rounded-full transition-colors text-left cursor-pointer"
              >
                + {chip}
              </button>
            ))}
          </div>
        </div>

        {/* Campo de Justificativa Livre */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Justificativa da Ausência de Exame Demissional <span className="text-red-500 font-bold">*</span>
          </label>
          <textarea
            required
            rows={3}
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
            placeholder="Descreva o motivo pelo qual o exame demissional não foi realizado ou por que o desligamento só está sendo informado agora (mínimo 10 caracteres)..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
          />
        </div>

        {/* Termo de Ciência e Auditoria (Cores Claras) */}
        <div className="bg-amber-50/70 p-3 rounded-lg border border-amber-200 flex items-start gap-3">
          <input
            type="checkbox"
            id="declaracao_ciencia"
            required
            checked={declaracaoCiencia}
            onChange={(e) => setDeclaracaoCiencia(e.target.checked)}
            className="mt-1 h-4 w-4 text-rose-600 focus:ring-rose-500 border-gray-300 rounded cursor-pointer"
          />
          <label
            htmlFor="declaracao_ciencia"
            className="text-xs text-amber-950 cursor-pointer select-none leading-relaxed"
          >
            <span className="font-semibold text-red-600">* Declaração de Ciência:</span> Declaro estar ciente de que
            o registro retroativo de desligamento sem exame demissional é de responsabilidade da empresa contratante
            e será arquivado com registro de data, hora e operador para fins de auditoria de SST.
          </label>
        </div>

        {/* Rodapé com Botões */}
        <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting || !declaracaoCiencia}
            className="px-5 py-2 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg shadow-sm flex items-center gap-2 transition-colors disabled:opacity-50 cursor-pointer"
          >
            <UserMinusIcon className="w-4 h-4" />
            {submitting ? 'Processando...' : 'Confirmar Desligamento'}
          </button>
        </div>
      </form>
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </Modal>
  );
};

export default ModalDesligamentoSemDemissional;
